/**
 * Generates a function that returns a document title using the provided title
 * first, followed by a subtitle (with specifiable fallback), and the existing
 * title (with specifiable fallback)
 *
 * Meant to be used with react-titled to set the document title.
 *
 * @example
 * ```tsx
 * const breadcrumbs: BreadcrumbProps[] = [
 *   { title: "Home" },
 *   { title: "Settings" },
 *   { title: "Profile" },
 * ];
 *
 * // the document title for this would be => "Dashboard | Profile | Validere App"
 * <Titled title="Validere App">
 *  <Titled title={generateDocumentTitle("Dashboard", breadcrumbs)}>
 *    <div>
 *      <h1>Dashboard</h1>
 *    </div>
 *  </Titled>
 * </Titled>
 * ```
 *
 * @param title - the page you want to title. set to empty string to use the
 * existing title or the fallback existing title.
 * @param subtitle - optional subtitle of the page
 * @param opts.fallbackExistingTitle - fallback title to use if the existing
 * title passed in is falsey
 * @returns a function that takes the existing title and returns this formatted
 * title
 */
export const generateDocumentTitle =
  (
    title: string,
    subtitle?: string,
    opts: {
      fallbackExistingTitle?: string;
    } = {}
  ) =>
  (existingTitle: string) => {
    return [title, subtitle, existingTitle || opts.fallbackExistingTitle]
      .filter(Boolean)
      .join(" | ");
  };
